import React, { useState, useEffect } from 'react';
import { Modal, Header, Icon, Button } from 'semantic-ui-react';
import { BaseModalProps, BaseModalTab } from './types';
import { DesignSystem, getButtonHoverEffects } from '../../../styles/designSystem';

export type { BaseModalAction } from './types';

/**
 * Modal base reutilizável com design padronizado para o sistema Gettel
 */
const BaseModal: React.FC<BaseModalProps> = ({
  isOpen,
  onClose,
  title,
  icon = 'window maximize',
  subtitle,
  size = 'medium',
  children,
  tabs,
  activeTab: externalActiveTab,
  onTabChange,
  actions = [],
  headerStyle,
  contentStyle,
  actionsStyle,
  closeOnDimmerClick = true,
  closeIcon = true,
}) => {
  // Estado interno para controlar a aba ativa (caso não seja controlado externamente)
  const [internalActiveTab, setInternalActiveTab] = useState<string | undefined>(
    tabs && tabs.length > 0 ? tabs[0].key : undefined
  );

  // Usa o activeTab externo se fornecido, caso contrário usa o estado interno
  const activeTab = externalActiveTab !== undefined ? externalActiveTab : internalActiveTab;

  // Atualiza o estado interno quando o activeTab externo muda
  useEffect(() => {
    if (externalActiveTab !== undefined) {
      setInternalActiveTab(externalActiveTab);
    }
  }, [externalActiveTab]);

  // Handler para mudança de aba
  const handleTabChange = (tabKey: string) => {
    if (onTabChange) {
      onTabChange(tabKey);
    } else {
      setInternalActiveTab(tabKey);
    }
  };

  // Determina o tamanho do modal com base na prop size
  const getModalSize = () => {
    switch (size) {
      case 'small':
        return 'small';
      case 'medium':
        return 'standard';
      case 'large':
        return 'large';
      case 'fullscreen':
        return 'fullscreen';
      default:
        return 'standard';
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size={getModalSize() as any}
      closeOnDimmerClick={closeOnDimmerClick}
      closeIcon={closeIcon}
      style={{
        background: DesignSystem.colors.lightPurple.gradient,
        borderRadius: DesignSystem.borderRadius.large,
        boxShadow: DesignSystem.shadows.modal,
        maxHeight: '90vh',
        margin: '2vh auto',
      }}
    >
      {/* Header do Modal */}
      <Modal.Header
        style={{
          background: 'linear-gradient(135deg, #2c3e50 0%, #000000 100%)',
          color: 'white',
          border: 'none',
          borderRadius: DesignSystem.components.modal.header.borderRadius,
          padding: '1rem 1.5rem',
          boxShadow: DesignSystem.components.modal.header.boxShadow,
          position: 'relative',
          overflow: 'hidden',
          ...headerStyle,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            position: 'relative',
            zIndex: 2,
          }}
        >
          <div
            style={{
              background: 'rgba(255,255,255,0.2)',
              borderRadius: '50%',
              padding: '0.3rem',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Icon name={icon as any} />
          </div>
          <div>
            <Header as="h2" style={{ color: 'white', margin: 0, fontWeight: '600', fontSize: '1.2rem' }}>
              {title}
            </Header>
            {subtitle && (
              <p style={{ color: 'rgba(255,255,255,0.8)', margin: '0.1rem 0 0 0', fontSize: '0.8rem' }}>
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {/* Elemento decorativo */}
        <div
          style={{
            position: 'absolute',
            top: '-50%',
            right: '-10%',
            width: '200px',
            height: '200px',
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '50%',
            zIndex: 1,
          }}
        />
      </Modal.Header>

      {/* Conteúdo do Modal */}
      <Modal.Content
        scrolling
        style={{
          background: DesignSystem.components.modal.content.background,
          padding: '0',
          borderRadius: tabs && tabs.length > 0 ? '0' : DesignSystem.components.modal.content.borderRadius,
          maxHeight: '70vh',
          ...contentStyle,
        }}
      >
        {/* Sistema de abas (opcional) */}
        {tabs && tabs.length > 0 && (
          <div
            style={{
              background: 'white',
              padding: '0.75rem 1rem',
              borderBottom: '1px solid #e9ecef',
              boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
              position: 'sticky',
              top: 0,
              zIndex: 10,
            }}
          >
            <Header
              as="h3"
              style={{
                color: '#2c3e50',
                marginBottom: '0.5rem',
                fontWeight: '600',
                textAlign: 'center',
                fontSize: '0.95rem',
              }}
            >
              <Icon name="chart bar" />
              Seções
            </Header>

            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '0.3rem',
                flexWrap: 'wrap',
              }}
            >
              {tabs.map((tab: BaseModalTab) => (
                <Button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  style={{
                    background: activeTab === tab.key
                      ? DesignSystem.colors.secondary.gradient
                      : 'white',
                    color: activeTab === tab.key ? 'white' : '#666',
                    border: `1px solid ${activeTab === tab.key ? '#4A90E2' : '#e9ecef'}`,
                    borderRadius: DesignSystem.borderRadius.large,
                    padding: '0.4rem 0.8rem',
                    fontWeight: DesignSystem.typography.fontWeight.semibold,
                    fontSize: '1.05rem',
                    boxShadow: activeTab === tab.key
                      ? DesignSystem.shadows.blueAccent
                      : DesignSystem.shadows.subtle,
                    transition: DesignSystem.transitions.normal,
                    transform: activeTab === tab.key ? 'translateY(-1px)' : 'translateY(0)',
                    minWidth: '85px',
                  }}
                >
                  <Icon name={tab.icon as any} />
                  {tab.label}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Conteúdo principal */}
        <div style={{ padding: tabs && tabs.length > 0 ? '0' : '1rem' }}>
          {children}
        </div>
      </Modal.Content>

      {/* Footer com ações */}
      {actions.length > 0 && (
        <Modal.Actions
          style={{
            background: DesignSystem.components.modal.actions.background,
            borderTop: '1px solid #e9ecef',
            padding: '1rem 1.5rem',
            justifyContent: 'center',
            gap: '0.5rem',
            borderRadius: DesignSystem.components.modal.actions.borderRadius,
            display: 'flex',
            ...actionsStyle,
          }}
        >
          {actions.map((action) => (
            <Button
              key={action.key}
              primary={action.primary}
              disabled={action.disabled}
              loading={action.loading}
              onClick={action.onClick}
              style={
                action.primary
                  ? DesignSystem.components.button.primary
                  : DesignSystem.components.button.secondary
              }
              {...(action.primary ? getButtonHoverEffects('primary') : getButtonHoverEffects('secondary'))}
            >
              {action.icon && <Icon name={action.icon as any} />}
              {action.content}
            </Button>
          ))}
        </Modal.Actions>
      )}
    </Modal>
  );
};

export default BaseModal;
